import React, { useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type { FormikProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import { tap } from 'rxjs/operators';
import type { IOrder } from 'v2/components/Pages/Orders/interfaces';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ordersService from 'v2/services/orders';
import { isRequired } from 'validations/defaultMessages';
import * as Yup from 'yup';

const trackingSchema = Yup.object().shape({
  traking_code: Yup.string().required(isRequired('código de rastreio'))
});

const List = () => {
  const { message } = App.useApp();
  const [alreadySent, setAlreadySent] = useState<number[]>([]);
  const [isLoading] = useObservable(() => ordersService.isLoading(), []);

  const [orders] = useObservable(() => ordersService.getOrdersToSendDeliveryCode(), []);

  const [onSendDeliveryCode] = useCallbackObservable(
    (values: IOrder, form: FormikProps<IOrder>) => {
      return ordersService.sendDeliveryCodeOrder(values).pipe(
        tap(() => {
          try {
            setAlreadySent(prev => [...prev, values.invoice_id]);
            form.setSubmitting(false);
            message.success(`Código de rastreio ${values.traking_code} enviado com sucesso`);
          } catch (error) {
            message.error(typeof error === 'string' ? error : 'Não foi possível enviar o código de rastreio.');
          }
        })
      );
    },
    [message]
  );

  const columns: ColumnsType<IOrder> = [
    { title: 'Fatura', dataIndex: 'invoice_id', key: 'invoice_id' },
    {
      title: 'Cliente',
      key: 'customer',
      responsive: ['md'],
      render: (_, order) => (
        <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
          {order.customer.name}
        </PermissionHide>
      )
    },
    {
      title: 'Cidade/Estado',
      key: 'city',
      responsive: ['md'],
      render: (_, order) => (
        <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
          {`${order.customer.address.city}/${order.customer.address.state}`}
        </PermissionHide>
      )
    },
    {
      title: 'CEP',
      key: 'zipCode',
      responsive: ['md'],
      render: (_, order) => (
        <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
          {order.customer.address.zip_code}
        </PermissionHide>
      )
    },
    {
      title: 'Ganho',
      key: 'income',
      align: 'right',
      render: (_, order) => (
        <>
          <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
            <FormattedNumber format='BRL' value={order.net_income} />
          </PermissionHide>
          <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
            <em>oculto</em>
          </PermissionHide>
        </>
      )
    },
    {
      title: 'Código de rastreio',
      key: 'trackingCode',
      width: 320,
      render: (_, order, index) => (
        <Formik
          key={`order-${order.invoice_id}`}
          initialValues={order}
          validationSchema={trackingSchema}
          onSubmit={onSendDeliveryCode}
        >
          {({ isSubmitting, values, dirty, isValid }: FormikProps<IOrder>) => {
            const sent = alreadySent?.includes(values.invoice_id);
            return (
              <Form>
                <div className='flex flex-row gap-2'>
                  <Field
                    placeholder='Código'
                    id={`tracking_code${index}`}
                    name={'traking_code'}
                    fullWidth
                    disabled={sent}
                    value={values.traking_code ? values.traking_code : ''}
                    InputLabelProps={{ shrink: true }}
                    component={TextFormField}
                  />

                  <Button
                    disabled={isSubmitting || !dirty || !isValid || !!sent}
                    htmlType='submit'
                    type='primary'
                    className='shrink-0'
                  >
                    {sent ? 'Enviado' : isSubmitting ? 'Enviando ...' : 'Enviar'}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={Array.isArray(orders) ? orders : []}
      rowKey='invoice_id'
      loading={isLoading}
      pagination={false}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'Nenhum registro encontrado.' }}
    />
  );
};
export default List;
